import {debounceTime} from 'rxjs/operators';
import {Component, OnInit, OnChanges, Input, OnDestroy} from '@angular/core';
import { UntypedFormGroup, UntypedFormArray, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {WizardWidgetComponent} from '../wizard-widget/wizard-widget.component';
import {TypesService, FormBuilderService, NotificationService} from '../../../services';
import {IndividualLiabilityModel} from '../../../models/widgets/liabilities-details-model';
import * as _ from 'lodash';
import {Decimal} from 'decimal.js';
import {BehaviorService} from '../../../services/behavior.service';
import { Subscription } from 'rxjs';
import {DataHandlerService} from '../../../services/data-handler.service';
import { OwnershipAllocationComponent } from '../ownership-allocation/ownership-allocation.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgIf, NgFor, TitleCasePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import {
  MatInputCommifiedDirective
} from '../../../../../firstmac-material-lib/projects/fmc-elements/src/lib/commified/mat-input-commified-directive';
import {
  FurtherAdvanceConfirmationComponent
} from '../further-advance-confirmation/further-advance-confirmation.component';

@Component({
    selector: 'app-liabilities-details',
    templateUrl: './liabilities-details.component.html',
    styles: [],
    standalone: true,
  imports: [MatCardModule, MatCardModule, NgIf, NgFor, MatFormFieldModule, MatFormFieldModule, MatInputModule, MatInputModule,
    MatCheckboxModule, MatCheckboxModule, FormsModule, ReactiveFormsModule, MatSelectModule, MatSelectModule, MatOptionModule,
    MatOptionModule, MatButtonModule, MatButtonModule, MatIconModule, OwnershipAllocationComponent, TitleCasePipe, MatInputCommifiedDirective, FurtherAdvanceConfirmationComponent]
})

export class LiabilitiesDetailsComponent extends WizardWidgetComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public formGroup: UntypedFormGroup;
  @Input() public investmentFormArray: UntypedFormArray;
  @Input() public personList: any;
  @Input() public companyList: any;
  @Input() public trustList: any;
  @Input() public isSMSF = false;
  @Input() public faType: string;
  @Input() public isFurtherAdvance = false;

  public liabilitiesFormArray: any;
  public allLenders = [];

  private _vxLiabilityTypesSlug = 'liability-types';
  private _lendersTypesSlug = 'credit-providers';

  public liabilityTypes: any;
  public creditProviders: any;

  private subscriptions: Subscription[] = [];

  constructor(public dataHandlerService: DataHandlerService,
    private typesService: TypesService,
    private notificationService: NotificationService,
    private formBuilderService: FormBuilderService,
    private behaviorService: BehaviorService
  ) {
    super(dataHandlerService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.liabilitiesFormArray = this.formGroup.get('liabilitiesList');
    Promise.all([
      this.populateLiabilities(),
      this.populateLenders(),
    ]).then(() => {
      console.log('init liabilities', this);
      this.initLiabilities();
      this.handleRentalOccupancyChange(this.personList.value);
    });

    if (this.investmentFormArray) {
      this.subscriptions.push(this.investmentFormArray.valueChanges.pipe(debounceTime(1000))
        .subscribe((val) => {
          console.debug('LiabilitiesDetailsComponent.investmentFormArray.valueChanges');
          this.computeTotalLiabilities();
        }));
    }

    // subscribe to person list to update
    if (this.personList) {
      this.subscriptions.push(this.personList.valueChanges.pipe(
        debounceTime(1000))
        .subscribe(val => {
          console.debug('LiabilitiesDetailsComponent.personList.valueChanges');
          this.handleRentalOccupancyChange(val);
        }));
    }
  }

  public handleRentalOccupancyChange(personListValue) {

    const forcedRental = v => v.getRawValue().force && v.getRawValue().type === 'rent';
    const personsWithRental = _.uniqBy(_.filter(personListValue, ['residencyType', 'renting']), 'homeAddress.fullAddress');
    let liabilitiesForRental = _.filter(this.liabilitiesFormArray.controls, forcedRental);

    while (liabilitiesForRental.length > personsWithRental.length) {
      // delete
      const liabilityIndexToDelete = _.findLastIndex(this.liabilitiesFormArray.controls, forcedRental);
      this.liabilitiesFormArray.removeAt(liabilityIndexToDelete);
      liabilitiesForRental = _.filter(this.liabilitiesFormArray.controls, forcedRental);
    }

    while (personsWithRental.length > liabilitiesForRental.length) {
      this.addLiability({
        force: true,
        type: 'rent',
        amount: 0,
      });
      liabilitiesForRental = _.filter(this.liabilitiesFormArray.controls, forcedRental);
    }

    // patch address data
    _.forEach(liabilitiesForRental, (control, idx) => {
      control.get('description').patchValue(_.get(personsWithRental, `${idx}.homeAddress.fullAddress`));
    });
  }


  ngOnChanges(changes) {
    this.liabilitiesFormArray = this.formGroup.get('liabilitiesList');
  }

  ngOnDestroy() {
    try {
      this.subscriptions.forEach((s) => s.unsubscribe());
      this.liabilitiesFormArray?.clear();
    } catch (error) {
      // to skip the error. but logging it to help in investigation.
      console.log('Error @onDestroy: ', error);
    }
  }



  public populateLiabilities() {
    return this.typesService.getTypesList(this._vxLiabilityTypesSlug)
      .then((results: any) => {
        this.liabilityTypes = results;
      });
  }

  public initLiabilities() {
    const existingLiabilities = this.liabilitiesFormArray.value;
    if (existingLiabilities.length === 0) {
      // this.addLiability(null);
    } else {
      while (this.liabilitiesFormArray.length) {
        this.liabilitiesFormArray.removeAt(0);
      }
      existingLiabilities.forEach((liab) => {
        this.addLiability(liab);
      });
    }
    this.computeTotalLiabilities();
  }

  public addLiability(data = null) {
    if (!this.isSMSF) {
      console.log('[addLiability]');
      const liability = new IndividualLiabilityModel();
      const fg = this.formBuilderService.convertObjectToFormGroup(liability);
      this.formBuilderService.setValidationRules(new IndividualLiabilityModel(), fg);
      this.liabilitiesFormArray.push(fg);
      if (data) {
        if (data.lender && data.lender.partyRoleId) {
          data.lender = data.lender.partyRoleId;
        }
        fg.patchValue(data);
        this.calcMonthly(fg, fg.value.limitValue);
      }
      return fg;
    }
  }

  public calcMonthly(liability, val) {
    // console.log('calcmonthly');
    if (liability.get('type').value === 'credit-card') {
      liability.get('amount').patchValue(Number(val * 0.032267).toFixed(2), {emitEvent: false});
      liability.get('postSettlement').patchValue(val, {emitEvent: false});
      this.computeTotalLiabilities();
    }
  }

  public removeLiability(index) {
    // console.log('[removeAsset]', index);
    this.liabilitiesFormArray.removeAt(index);
    this.computeTotalLiabilities();
  }

  public computeTotalLiabilities() {
    // console.log('[computeTotalLiabilities]', this.liabilitiesFormArray.value, this.investmentFormArray.value);
    let total = new Decimal(0);
    this.liabilitiesFormArray.controls.forEach((liability) => {
      total = total.add(liability.value.amount || 0);
    });

    if (this.investmentFormArray) {
      this.investmentFormArray.controls.forEach((ctrl: UntypedFormGroup) => {
        if (!ctrl.getRawValue().isNewInvestment) {
          total = total.add(ctrl.getRawValue().monthlyRepayments || 0);
        }
      });
    }

    this.formGroup.get('totalLiabilitiesValue').patchValue(total.toNumber());
  }

  public allowExtValue(id, propertyName) {
    const type = this.liabilityTypes.find(t => t.id === id);
    return !!(type && type.ext && type.ext[propertyName]);
  }

  public allowInterestOnly(id) {
    const type = this.liabilityTypes.find(t => t.id === id);
    return !!(type && type.ext && type.ext.interestOnly);
  }

  public getLenderName(lenderPartyRoleId) {
    return _.get(this.allLenders.find(t => Number(t.partyRoleId) === Number(lenderPartyRoleId)), 'name', '');
  }


  public populateLenders() {
    // console.log('[populateLenders]');
    return this.typesService.getTypesList(this._lendersTypesSlug, 'ext.CompanyName')
      .then((results) => {
        // console.log('[populateLenders]', results.length);
        this.allLenders = results.sort((a, b) => {
          return a.text - b.text;
        })
          .map((t) => {
            return {
              name: t.text,
              partyRoleId: t.id,
              partyId: t.ext.PartyID
            };
          });
      });
  }

  handleLiabilityTypeChange(formGroup: UntypedFormGroup, type: String, index: number) {
    if (type === 'investment') {
      this.navigator.next('app-investment-income'); // 0 - based index
      this.liabilitiesFormArray.removeAt(index);
      this.behaviorService.triggerRentalIncomeBehavior(BehaviorService.ADD_RENTAL_INCOME_BEHAVIOR);
      this.notificationService.toast('warn', 'Please input investment income details in "Rental Investment Incomes" panel.');
    }
  }
}
